import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Alert,
  AlertTitle
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import SocialSecuritySection from '../calculators/SocialSecuritySection';
import GlobalSettingsForm from './GlobalSettingsForm';
import SectionNavigation from '../common/SectionNavigation';

const AdvancedSettingsSection = ({ 
  onNextSection, 
  onPreviousSection, 
  currentSectionIndex, 
  sectionNames 
}) => {
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ 
        color: '#34517a', 
        fontWeight: 600, 
        marginBottom: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <InfoIcon sx={{ fontSize: '2rem', color: '#34517a' }} />
        Advanced Settings
      </Typography>

      {/* Info Alert */}
      <Alert severity="info" sx={{ marginBottom: 3 }}>
        <AlertTitle>Optional Configuration</AlertTitle>
        These settings use sensible defaults and are optional for most users. 
        The calculator will work perfectly without adjusting these values.
      </Alert>

      {/* Social Security Benefits Accordion */}
      <Accordion 
        expanded={expandedPanel === 'social-security'} 
        onChange={handleAccordionChange('social-security')}
        sx={{ marginBottom: 2, border: '1px solid #e0e0e0' }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            backgroundColor: '#f8f9fa',
            '&.Mui-expanded': { backgroundColor: '#e3f2fd' }
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#34517a' }}>
              🏛️ Social Security Benefits
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', marginTop: 0.5 }}>
              Configure Social Security integration (uses standard calculations by default)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <SocialSecuritySection 
            // Remove navigation buttons since we're in accordion
            onNextSection={null} 
            onPreviousSection={null}
            currentSectionIndex={null}
            sectionNames={null}
            isEmbedded={true} // Flag to hide navigation in embedded mode
          />
        </AccordionDetails>
      </Accordion>

      {/* Global Settings Accordion */}
      <Accordion 
        expanded={expandedPanel === 'global-settings'} 
        onChange={handleAccordionChange('global-settings')}
        sx={{ marginBottom: 2, border: '1px solid #e0e0e0' }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            backgroundColor: '#f8f9fa',
            '&.Mui-expanded': { backgroundColor: '#e3f2fd' }
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#34517a' }}>
              ⚙️ Global Settings
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', marginTop: 0.5 }}>
              Adjust calculation parameters (COLA, growth rates, etc.)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <GlobalSettingsForm 
            // Remove navigation buttons since we're in accordion
            onNextSection={null} 
            onPreviousSection={null}
            currentSectionIndex={null}
            sectionNames={null}
            isEmbedded={true} // Flag to hide navigation in embedded mode
          />
        </AccordionDetails>
      </Accordion>

      {/* Section Navigation */}
      <SectionNavigation
        onNextSection={onNextSection}
        onPreviousSection={onPreviousSection}
        currentSectionIndex={currentSectionIndex}
        sectionNames={sectionNames}
      />
    </Box>
  );
};

export default AdvancedSettingsSection;